import React from 'react';
import { cn } from '../lib/utils';

// Badge 변형 타입들
type BadgeVariant = 
  | 'default' 
  | 'secondary' 
  | 'success' 
  | 'warning' 
  | 'danger' 
  | 'info'
  | 'outline'
  | 'ghost';

type BadgeSize = 'xs' | 'sm' | 'md' | 'lg';

// 색상 스킴 타입
interface ColorScheme {
  bg: string;
  text: string;
  border?: string;
}

// 미리 정의된 색상 스킴들
const colorSchemes: Record<string, ColorScheme> = {
  // 기본 변형들
  default: {
    bg: 'bg-gray-100 dark:bg-gray-700',
    text: 'text-gray-700 dark:text-gray-300'
  },
  secondary: {
    bg: 'bg-gray-200 dark:bg-gray-600',
    text: 'text-gray-800 dark:text-gray-200'
  },
  success: {
    bg: 'bg-green-100 dark:bg-green-900/30',
    text: 'text-green-700 dark:text-green-300'
  },
  warning: {
    bg: 'bg-yellow-100 dark:bg-yellow-900/30',
    text: 'text-yellow-700 dark:text-yellow-300'
  },
  danger: {
    bg: 'bg-red-100 dark:bg-red-900/30',
    text: 'text-red-700 dark:text-red-300'
  },
  info: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-700 dark:text-blue-300'
  },
  
  // Outline 변형들
  'outline-default': {
    bg: 'bg-transparent',
    text: 'text-gray-700 dark:text-gray-300',
    border: 'border border-gray-300 dark:border-gray-600'
  },
  'outline-success': {
    bg: 'bg-transparent',
    text: 'text-green-700 dark:text-green-300',
    border: 'border border-green-300 dark:border-green-600'
  },
  'outline-warning': {
    bg: 'bg-transparent',
    text: 'text-yellow-700 dark:text-yellow-300',
    border: 'border border-yellow-300 dark:border-yellow-600'
  },
  'outline-danger': {
    bg: 'bg-transparent',
    text: 'text-red-700 dark:text-red-300',
    border: 'border border-red-300 dark:border-red-600'
  },
  'outline-info': {
    bg: 'bg-transparent',
    text: 'text-blue-700 dark:text-blue-300',
    border: 'border border-blue-300 dark:border-blue-600'
  },

  // 특수 색상들 (Jamake용)
  black: {
    bg: 'bg-black dark:bg-gray-800',
    text: 'text-white'
  },
  purple: {
    bg: 'bg-purple-100 dark:bg-purple-900/30',
    text: 'text-purple-700 dark:text-purple-300'
  },
  orange: {
    bg: 'bg-orange-100 dark:bg-orange-900/30',
    text: 'text-orange-700 dark:text-orange-300'
  }
};

interface BadgeProps {
  children: React.ReactNode;
  variant?: BadgeVariant;
  size?: BadgeSize;
  className?: string;
  colorScheme?: string; // 커스텀 색상 스킴
  customColors?: ColorScheme; // 완전 커스텀 색상
  onClick?: () => void;
  removable?: boolean;
  onRemove?: () => void;
}

const Badge = ({
  children,
  variant = 'default',
  size = 'sm',
  className,
  colorScheme,
  customColors,
  onClick,
  removable = false,
  onRemove
}: BadgeProps) => {
  // 사이즈별 클래스
  const sizeClasses = {
    xs: 'px-1.5 py-0.5 text-xs',
    sm: 'px-2 py-1 text-xs',
    md: 'px-2.5 py-1.5 text-sm',
    lg: 'px-3 py-2 text-base'
  };

  // 색상 결정
  const getColors = (): ColorScheme => {
    if (customColors) return customColors;
    if (colorScheme && colorSchemes[colorScheme]) return colorSchemes[colorScheme];
    
    // Outline 변형 처리
    if (variant === 'outline') {
      return colorSchemes['outline-default'];
    }
    
    return colorSchemes[variant] || colorSchemes.default;
  };

  const colors = getColors();

  return (
    <span
      className={cn(
        'inline-flex items-center font-medium rounded-full transition-colors',
        sizeClasses[size],
        colors.bg,
        colors.text,
        colors.border,
        onClick && 'cursor-pointer hover:opacity-80',
        className
      )}
      onClick={onClick}
    >
      {children}
      {removable && onRemove && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="ml-1 hover:bg-black/10 dark:hover:bg-white/10 rounded-full p-0.5 transition-colors"
        >
          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      )}
    </span>
  );
};

// Badge 그룹 컴포넌트
interface BadgeGroupProps {
  children: React.ReactNode;
  className?: string;
  spacing?: 'tight' | 'normal' | 'relaxed';
}

const BadgeGroup = ({ 
  children, 
  className,
  spacing = 'normal'
}: BadgeGroupProps) => {
  const spacingClasses = {
    tight: 'gap-1',
    normal: 'gap-2',
    relaxed: 'gap-3'
  };

  return (
    <div className={cn('flex flex-wrap', spacingClasses[spacing], className)}>
      {children}
    </div>
  );
};

// 상태별 Badge 생성 헬퍼 함수들
const createStatusBadge = (status: string): Partial<BadgeProps> => {
  const statusMap: Record<string, Partial<BadgeProps>> = {
    '등록됨': { variant: 'default' },
    '진행중': { variant: 'info' },
    '완료': { variant: 'success' },
    '반려': { variant: 'danger' },
    '취소': { variant: 'danger' }
  };

  return statusMap[status] || { variant: 'default' };
};

const createTagBadge = (tag: string): Partial<BadgeProps> => {
  const tagMap: Record<string, Partial<BadgeProps>> = {
    '블랙': { colorScheme: 'black' },
    '원스텝': { colorScheme: 'purple' },
    '특수작업': { colorScheme: 'orange' }
  };

  return tagMap[tag] || { variant: 'default' };
};

// Compound 컴포넌트로 그룹 연결
(Badge as any).Group = BadgeGroup;

export { Badge, createStatusBadge, createTagBadge };
export type { BadgeProps, BadgeGroupProps, BadgeVariant, BadgeSize, ColorScheme };
